// PURGE-RETIRED-AGENTS: "Remove permanently" for a retired DE or AG (owners and admins). The
// dialog reads the plan first (`…/purge-plan`): what goes, what stays under "Removed agent
// (<name>)", where its open tasks go and what happens to its memory. "Download its data" saves
// the plan's own data as JSON before anything happens; the removal needs the name typed.
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Checkbox,
  InfoTip,
  Input,
  Label,
  toast,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DownloadIcon } from "lucide-react";
import { type ReactNode, useId, useState } from "react";
import type { Api } from "../../api/client";
import { problemText } from "../de/de-status";
import { saveBlob } from "../finance/finance-parts";
import { keys } from "../queries";

export type PurgeTarget = {
  kind: "digital_employee" | "workforce_agent";
  id: string;
  name: string;
};

const GOES: Record<string, string> = {
  record: "Agent record",
  seat_assignments: "Seat history",
  keys: "Keys",
  endpoints: "Endpoints",
  profile_versions: "Profile versions",
  sub_agents: "Sub-agent profiles",
  skills: "Skills and helpers",
  configuration: "Job settings",
  access: "Channels and access",
  wakes: "Pending wakes",
  runs: "Unfinished runs",
};
const KEPT: Record<string, string> = {
  open_tasks: "Open tasks",
  messages: "Messages and comments",
  events: "Audit events",
  cost_events: "Spend and cost",
  kpi_readings: "KPI readings",
  finished_runs: "Finished runs",
};

const lines = (counts: Record<string, number>, labels: Record<string, string>, keep = false) =>
  Object.entries(labels)
    .filter(([k]) => k in counts && (k === "record" || keep || (counts[k] ?? 0) > 0))
    .map(([k, label]) => ({ k, label, n: counts[k] ?? 0 }));

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;
const total = (counts: Record<string, number>) => Object.values(counts).reduce((s, n) => s + n, 0);

/** The plan, the confirmation and the removal of one retired agent (render `dialog` once). */
export function usePurge(
  api: Api,
  companyId: string,
  target: PurgeTarget,
): { start: () => void; dialog: ReactNode } {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState("");
  const [deleteMemory, setDeleteMemory] = useState(false);
  const id = useId();
  const de = target.kind === "digital_employee";
  const name = workerName(target.name);
  const plan = useQuery({
    queryKey: ["purge-plan", companyId, target.kind, target.id],
    queryFn: () =>
      de
        ? api.client.digitalEmployees.purgePlan({ params: { co: companyId, id: target.id } })
        : api.client.workforceAgents.purgePlan({ params: { co: companyId, id: target.id } }),
    enabled: open,
    staleTime: 0,
  });
  const purge = useMutation({
    mutationFn: () => {
      const token = plan.data?.memory.recall.delete?.confirm_token;
      const body = {
        confirm: typed,
        ...(deleteMemory && token ? { delete_memory: true, memory_confirm_token: token } : {}),
      };
      const params = { co: companyId, id: target.id };
      return de
        ? api.client.digitalEmployees.purge({ params, body })
        : api.client.workforceAgents.purge({ params, body });
    },
    onSuccess: () => {
      setOpen(false);
      toast.add({ title: `${name} removed permanently` });
      void qc.invalidateQueries({ queryKey: keys.digitalEmployees(companyId) });
      void qc.invalidateQueries({ queryKey: keys.workforceAgents(companyId) });
    },
  });
  const p = plan.data;
  const said = typed.trim().toLowerCase();
  const confirmed = said === name.trim().toLowerCase() || said === target.name.trim().toLowerCase();
  const download = () => {
    if (!p) return;
    const file = {
      exported_at: new Date().toISOString(),
      ...p.data,
      plan: { ...p, data: undefined },
    };
    saveBlob(
      new Blob([JSON.stringify(file, null, 2)], { type: "application/json" }),
      `${name.replace(/[^\w.-]+/g, "-").toLowerCase()}-data.json`,
    );
  };
  const dialog = (
    <AlertDialog
      open={open}
      onOpenChange={(o) => {
        if (!o && !purge.isPending) setOpen(false);
      }}
    >
      <AlertDialogContent data-slot="purge-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle className="inline-flex items-center gap-1.5">
            Remove {name} permanently?
            <InfoTip label="About removing permanently">
              Deletes the agent from CompanyOS for good. Its work and history stay, shown as{" "}
              {p?.removed_label ?? "Removed agent"}. This cannot be undone.
            </InfoTip>
          </AlertDialogTitle>
        </AlertDialogHeader>
        {plan.isPending ? (
          <p className="text-sm text-muted-foreground">Reading what it has…</p>
        ) : plan.isError ? (
          <div role="alert" className="flex items-center gap-2 text-sm text-danger-text">
            {problemText(plan.error, "The plan could not be read.")}
            <Button size="xs" variant="secondary" onClick={() => void plan.refetch()}>
              Retry
            </Button>
          </div>
        ) : p ? (
          <div className="flex flex-col gap-3 text-sm" data-slot="purge-plan">
            {p.blocked ? (
              <p role="alert" className="text-danger-text" data-slot="purge-blocked">
                {p.blocked.detail}
              </p>
            ) : null}
            <div className="grid gap-3 sm:grid-cols-2">
              <section data-slot="purge-goes">
                <h3 className="mb-1 flex h-5 items-center text-xs font-medium text-muted-foreground">
                  Deleted
                </h3>
                <ul className="flex flex-col gap-0.5">
                  {lines(p.removes, GOES).map((l) => (
                    <li key={l.k} className="flex justify-between gap-2">
                      <span>{l.label}</span>
                      <span className="tabular-nums text-muted-foreground">
                        {l.k === "record" ? "" : l.n}
                      </span>
                    </li>
                  ))}
                </ul>
              </section>
              <section data-slot="purge-kept">
                <h3 className="mb-1 flex h-5 items-center gap-1 text-xs font-medium text-muted-foreground">
                  Kept
                  <InfoTip label="About what is kept">Shown as “{p.removed_label}”.</InfoTip>
                </h3>
                <ul className="flex flex-col gap-0.5">
                  {lines(p.keeps, KEPT, true).map((l) => (
                    <li key={l.k} className="flex justify-between gap-2">
                      <span>{l.label}</span>
                      <span className="tabular-nums text-muted-foreground">{l.n}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
            {de && p.tasks.open > 0 ? (
              <p data-slot="purge-tasks">
                {p.tasks.to
                  ? `${p.tasks.move} open task${p.tasks.move === 1 ? "" : "s"} → ${p.tasks.to.holder_name ?? p.tasks.to.seat_title}`
                  : `${p.tasks.open} open task${p.tasks.open === 1 ? "" : "s"} stay on its seat`}
              </p>
            ) : null}
            {de ? (
              <div className="flex flex-col gap-1.5" data-slot="purge-memory">
                <span className="inline-flex items-center gap-1">
                  Memory: {plural(p.memory.spaces.length, "space")}
                  {p.memory.recall.archive
                    ? `, ${plural(total(p.memory.recall.archive), "note")}`
                    : ""}{" "}
                  archived
                  <InfoTip label="About its memory">
                    Its memory agent is revoked, the spaces only it used and its notes in shared
                    spaces are archived (restorable). Ticked, its memory agent is deleted in Recall
                    with its own space, copies, keys and grants. This cannot be undone.
                  </InfoTip>
                </span>
                {p.memory.recall.state === "held" ? (
                  <p role="alert" className="text-danger-text" data-slot="purge-memory-held">
                    A legal hold keeps its memory. It is archived only.
                  </p>
                ) : null}
                {p.memory.recall.delete ? (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id={`${id}-memory`}
                      checked={deleteMemory}
                      onCheckedChange={(on) => setDeleteMemory(on === true)}
                    />
                    <Label htmlFor={`${id}-memory`} className="font-normal">
                      Also delete its memory
                    </Label>
                    {deleteMemory ? (
                      <span
                        className="text-xs text-muted-foreground"
                        data-slot="purge-memory-counts"
                      >
                        {Object.entries(p.memory.recall.delete.counts)
                          .filter(([, n]) => n > 0)
                          .map(([k, n]) => `${n} ${k}`)
                          .join(" · ")}
                      </span>
                    ) : null}
                  </div>
                ) : null}
              </div>
            ) : null}
            <div>
              <Button size="sm" variant="secondary" onClick={download} data-slot="purge-download">
                <DownloadIcon aria-hidden="true" />
                Download its data first
              </Button>
            </div>
            {p.blocked ? null : (
              <div className="flex flex-col gap-1.5" data-slot="purge-confirm">
                <label htmlFor={id} className="text-xs text-muted-foreground">
                  Type <strong className="text-foreground">{name}</strong> to confirm
                </label>
                <Input
                  id={id}
                  autoComplete="off"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                />
              </div>
            )}
          </div>
        ) : null}
        {purge.isError ? (
          <p role="alert" className="text-sm text-danger-text" data-slot="purge-error">
            {problemText(purge.error, `${name} could not be removed.`)}
          </p>
        ) : null}
        <AlertDialogFooter>
          <AlertDialogCancel disabled={purge.isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={purge.isPending || !p || Boolean(p.blocked) || !confirmed}
            onClick={() => purge.mutate()}
          >
            {purge.isPending ? "Removing…" : "Remove permanently"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
  return {
    start: () => {
      purge.reset();
      setTyped("");
      setDeleteMemory(false);
      setOpen(true);
    },
    dialog,
  };
}
